import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import type { ListDiff, ListDiffEqual } from './diffOrderedLists';
import { diffOrderedUniqueLists } from './diffOrderedUniqueLists';
import type { DiffableArray } from './types';

export interface KeyedListDiffModified<BeforeValue, AfterValue> {
    op: '~';
    before: BeforeValue;
    after: AfterValue;
}

export type KeyedListDiff<BeforeValue, AfterValue = BeforeValue> =
    | ListDiff<BeforeValue, AfterValue>
    | KeyedListDiffModified<BeforeValue, AfterValue>;

type GetIdStrategy<BeforeValue, AfterValue> =
    | {
          getId: (value: BeforeValue | AfterValue) => string;
      }
    | {
          getIdBefore: (value: BeforeValue) => string;
          getIdAfter: (value: AfterValue) => string;
      };

/**
 * Compute the diff between 2 ordered keyed lists.
 */
export function diffKeyedLists<BeforeValue, AfterValue>(
    before: DiffableArray<BeforeValue>,
    after: DiffableArray<AfterValue>,
    strategy: GetIdStrategy<BeforeValue, AfterValue> & {
        areEquals: (a: BeforeValue, b: AfterValue) => boolean;
    }
): Array<KeyedListDiff<BeforeValue, AfterValue>> {
    const getIdBefore = 'getId' in strategy ? strategy.getId : strategy.getIdBefore;
    const getIdAfter = 'getId' in strategy ? strategy.getId : strategy.getIdAfter;

    const beforeIndex = new Map<string, BeforeValue>();
    const afterIndex = new Map<string, AfterValue>();

    const beforeKeys: string[] = [];
    const afterKeys: string[] = [];

    for (let index = 0; index < before.length; index += 1) {
        const item = before.at(index);
        if (item !== undefined) {
            const key = getIdBefore(item);
            beforeIndex.set(key, item);
            beforeKeys.push(key);
        }
    }

    for (let index = 0; index < after.length; index += 1) {
        const item = after.at(index);
        if (item !== undefined) {
            const key = getIdAfter(item);
            afterIndex.set(key, item);
            afterKeys.push(key);
        }
    }

    const keyDiffs = diffOrderedUniqueLists(beforeKeys, afterKeys);
    const results: KeyedListDiff<BeforeValue, AfterValue>[] = [];

    keyDiffs.forEach((diff) => {
        switch (diff.op) {
            case '+': {
                results.push({
                    op: '+',
                    values: diff.values.map((key) => {
                        const value = afterIndex.get(key);
                        assertIsDefined(value);
                        return value;
                    }),
                });
                break;
            }
            case '-': {
                results.push({
                    op: '-',
                    values: diff.values.map((key) => {
                        const value = beforeIndex.get(key);
                        assertIsDefined(value);
                        return value;
                    }),
                });
                break;
            }
            case '=': {
                let pending: ListDiffEqual<BeforeValue> = {
                    op: '=',
                    values: [],
                };

                diff.values.forEach((key) => {
                    const after = afterIndex.get(key);
                    const before = beforeIndex.get(key);
                    assertIsDefined(after);
                    assertIsDefined(before);

                    if (strategy.areEquals(before, after)) {
                        pending.values.push(before);
                    } else {
                        if (pending.values.length > 0) {
                            results.push(pending);
                            pending = {
                                op: '=',
                                values: [],
                            };
                        }

                        results.push({
                            op: '~',
                            before,
                            after,
                        });
                    }
                });

                if (pending.values.length > 0) {
                    results.push(pending);
                    pending = {
                        op: '=',
                        values: [],
                    };
                }
                break;
            }
            default:
                assertNever(diff);
        }
    });

    return results;
}
